import { useState } from "react";
import { X, Send, Loader2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const TEAM_SIZES = ["1-5", "6-15", "16-50", "51-200", "200+"];

export default function RequestAccessModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", org: "", role: "", team_size: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErr("");
    setSubmitting(true);
    try {
      const res = await api.post("/public/request-access", form);
      setDone(res.data?.message || "Request received.");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit — try emailing sales@defdossier.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
      data-testid="request-access-modal"
    >
      <div
        className="flat-card w-full max-w-lg p-6 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ borderColor: "var(--cyan)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--text-2)] hover:text-white"
          aria-label="Close"
          data-testid="request-access-close"
        >
          <X size={18} />
        </button>

        <div className="overline mb-2">// REQUEST_ACCESS</div>
        <h2 className="font-display font-bold text-2xl uppercase mb-2">Request Full Access</h2>
        <p className="text-sm text-[var(--text-2)] mb-5">
          DefDossier is invite-only. Tell us a little about your team and sales will reach out within one business day with a pilot link.
        </p>

        {done ? (
          <div className="text-center py-8" data-testid="request-access-success">
            <CheckCircle2 size={48} className="mx-auto text-[var(--cyan)] mb-3" strokeWidth={1.5} />
            <div className="font-display uppercase text-lg mb-1">Request Received</div>
            <p className="text-sm text-[var(--text-2)]">{done}</p>
            <button onClick={onClose} className="btn-ghost mt-6" data-testid="request-access-done">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input required type="text" placeholder="Full name" value={form.name} onChange={set("name")}
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-sm" data-testid="req-name" />
            <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")}
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-sm" data-testid="req-email" />
            <input required type="text" placeholder="Organisation" value={form.org} onChange={set("org")}
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-sm" data-testid="req-org" />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Your role (optional)" value={form.role} onChange={set("role")}
                className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-sm" data-testid="req-role" />
              <select value={form.team_size} onChange={set("team_size")}
                className="bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-sm" data-testid="req-team-size">
                <option value="">Team size…</option>
                {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <textarea rows={3} placeholder="What are you trying to evidence for? (optional)" value={form.message} onChange={set("message")}
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-sm" data-testid="req-message" />

            {err && (
              <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="req-error">
                {err}
              </div>
            )}

            <button type="submit" disabled={submitting}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="req-submit">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {submitting ? "Sending…" : "Send Request"}
            </button>
            <p className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] text-center">
              Prefer email? <a href="mailto:sales@defdossier.com" className="text-[var(--cyan)] hover:underline">sales@defdossier.com</a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
